import {
  IS_ELECTRON,
  MARK_AS_READ_ON_FOCUS_DELAY,
  NODE_ENV_LOCAL_OR_DEVELOPMENT,
} from 'Constants/env';
import {
  STORE_MESSAGE,
  STORE_NOTIFICATION,
  STORE_PARTICIPANT,
  STORE_PERSON,
  STORE_UI,
} from 'Constants/stores';
import * as localforage from 'localforage';
import { has, isEmpty, throttle } from 'lodash';
import { inject, observer } from 'mobx-react';
import { now } from 'mobx-utils';
import * as React from 'react';
import { getCurrentConversationId } from 'Utils/getCurrentConversationId';
import { isNullOrUndefined } from 'Utils/typeGuards';
import { UiBridgeProps } from './interfaces';

export class UiBridge extends React.Component<UiBridgeProps> {
  /** Pending timer that marks the open conversation as read after focus. */
  private markAsReadTimeout: ReturnType<typeof setTimeout> | null = null;

  /** Cancel a scheduled mark-as-read (e.g. the window blurred before the delay elapsed). */
  private clearPendingMarkAsRead = () => {
    if (this.markAsReadTimeout !== null) {
      clearTimeout(this.markAsReadTimeout);
      this.markAsReadTimeout = null;
    }
  };

  /** Mark the currently open conversation as read on the server, then clear the local badge. */
  private markCurrentConversationAsRead = async () => {
    const { ui, message, participant } = this.props;
    const conversationId = getCurrentConversationId();
    if (isEmpty(conversationId)) {
      return;
    }

    // Mark the conversation as read on the server before clearing the badge.
    try {
      const groupedMessages =
        message.selectGroupedMessagesForConversation(conversationId);
      if (groupedMessages?.NewestMessageId) {
        await participant.updateMyLastReadMessage(
          conversationId,
          groupedMessages.NewestMessageId
        );
      }
    } catch (e) {
      console.error(
        `markCurrentConversationAsRead could not update last read Message for ${conversationId}`,
        e
      );
    }
    await ui.setConversationAndTotalUnreadCount(conversationId, 0, null, 0);
  };

  private onWindowFocus = () => {
    const { ui, notification } = this.props;
    ui.setLastFocus(now());

    // Delay marking as read so the user has a moment to notice the unread badge
    // Re-scheduled on each focus and cancelled if the window blurs again first
    this.clearPendingMarkAsRead();
    this.markAsReadTimeout = setTimeout(() => {
      this.markAsReadTimeout = null;
      void this.markCurrentConversationAsRead();
    }, MARK_AS_READ_ON_FOCUS_DELAY);

    notification.closeAllWebNotifications();
  };

  private onWindowBlur = () => {
    this.clearPendingMarkAsRead();
    this.props.ui.setLastBlur(now());
  };

  private checkAndSetFocus = () => {
    if (document.hasFocus()) {
      this.onWindowFocus();
    } else {
      this.onWindowBlur();
    }
  };

  private onVisibilityChange = () => {
    if (!document.hidden) {
      this.onWindowFocus();
    } else {
      this.onWindowBlur();
    }
  };

  private onMouseActivity = (e: MouseEvent) => {
    this.props.ui.setLastKeyboardMouseActivity(now());
  };

  private onMouseActivityThrottled = throttle(this.onMouseActivity, 200);

  private onKeyboardActivity = (e: KeyboardEvent) => {
    this.props.ui.setLastKeyboardMouseActivity(now());
  };

  private onKeyboardActivityThrottled = throttle(this.onKeyboardActivity, 200);

  /** Triggers an OffLine Presence status update on tab/window close*/
  onBrowserWindowClose = async () => {
    const { message, participant, person, ui } = this.props;
    if (person.IsLoggedIn) {
      if (NODE_ENV_LOCAL_OR_DEVELOPMENT) {
        console.debug('onBrowserWindowClose reporting OffLine Presence');
      }
      try {
        const voxeetSessionId = await localforage.getItem<string>(
          'voxeetSession'
        );
        ui.updatePresence('OffLine', false, voxeetSessionId);
      } catch (e) {
        console.error(
          'onBrowserWindowClose Could not update presence to OffLine.'
        );
      }

      const currentConversationId = getCurrentConversationId();
      if (!isNullOrUndefined(currentConversationId)) {
        try {
          const convGroupedMsgsCtr =
            message.selectGroupedMessagesForConversation(currentConversationId);
          if (!isNullOrUndefined(convGroupedMsgsCtr)) {
            await participant.updateMyLastReadMessage(
              currentConversationId,
              convGroupedMsgsCtr.NewestMessageId
            );
            await ui.setConversationAndTotalUnreadCount(
              currentConversationId,
              0,
              null,
              0
            );
            if (NODE_ENV_LOCAL_OR_DEVELOPMENT) {
              console.debug(
                `onBrowserWindowClose updateLastReadMessage for ${currentConversationId} to ${convGroupedMsgsCtr.NewestMessageId}, set unread counts to 0.`
              );
            }
          } else {
            console.warn(
              `onBrowserWindowClose could not retrieve Messages for ${currentConversationId}, and will not update its last read Message.`
            );
          }
        } catch (e) {
          console.error(
            'onBrowserWindowClose Could not update last read Message for',
            currentConversationId
          );
        }
      } else {
        console.info(
          'onBrowserWindowClose was not able to retrieve a current Conversation Id. This is not an error if there was no Conversation selected.'
        );
      }
    } else {
      console.info('onBrowserWindowClose user is not logged in.');
    }
    return null;
  };

  componentDidMount() {
    this.checkAndSetFocus();
    this.onVisibilityChange();
    window.addEventListener('focus', this.onWindowFocus);
    window.addEventListener('blur', this.onWindowBlur);
    document.addEventListener('visibilitychange', this.onVisibilityChange);
    document.addEventListener('mousemove', this.onMouseActivityThrottled);
    document.addEventListener('keypress', this.onKeyboardActivityThrottled);
    // Inside an Electron renderer process
    if (IS_ELECTRON && has(window, 'ipcRenderer')) {
      const ipcRenderer = (window as any).ipcRenderer; // as typeof import('electron').ipcRenderer but this causes a build failure so yeah.
      ipcRenderer.on('window-close', this.onBrowserWindowClose);
    } else {
      window.addEventListener('beforeunload', this.onBrowserWindowClose);
    }
  }

  componentWillUnmount() {
    this.clearPendingMarkAsRead();
    window.removeEventListener('focus', this.onWindowFocus);
    window.removeEventListener('blur', this.onWindowBlur);
    document.removeEventListener('visibilitychange', this.onVisibilityChange);
    document.removeEventListener('mousemove', this.onMouseActivityThrottled);
    document.removeEventListener('keypress', this.onKeyboardActivityThrottled);
  }

  render() {
    return <></>;
  }
}

export default inject(
  STORE_MESSAGE,
  STORE_NOTIFICATION,
  STORE_PARTICIPANT,
  STORE_PERSON,
  STORE_UI
)(observer(UiBridge));
